import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ProjectGovernanceApprovalKind,
  ProjectGovernanceDecision,
  type ProjectGovernanceDto,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

export function GovernanceDecisionForm({
  projectId,
  governance,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const kinds = governance.availableDecisions;

  const schema = yup
    .object({
      kind: yup.string().required(),
      rationale: yup.string().trim().required(t('workspace.governance.decision.rationaleRequired')).max(4000),
      followOnActions: yup.string().default(''),
    })
    .required();

  const { control, register, handleSubmit, reset, formState, watch } = useForm({
    resolver: yupResolver(schema),
    values: { kind: kinds[0] ?? '', rationale: '', followOnActions: '' },
  });
  const kind = watch('kind');
  const showFollowOn =
    kind === ProjectGovernanceApprovalKind.Closure || kind === ProjectGovernanceApprovalKind.PrematureClosure;

  const mutation = useGovernanceMutation(
    projectId,
    (v: { kind: string; rationale: string; followOnActions: string; decision: ProjectGovernanceDecision }) =>
      projectGovernanceApi.decide(projectId, {
        kind: v.kind as ProjectGovernanceApprovalKind,
        decision: v.decision,
        rationale: v.rationale,
        expectedVersion: governance.version,
        followOnActions: v.followOnActions
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      }),
    () => reset(),
  );

  const decide = (decision: ProjectGovernanceDecision) =>
    handleSubmit((v) => mutation.mutate({ ...v, decision }));

  return (
    <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
      <div className="space-y-1">
        <Label>{t('workspace.governance.decision.kind')}</Label>
        <Controller
          control={control}
          name="kind"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {t(`workspace.governance.decisionKind.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div className="space-y-1">
        <Label>{t('workspace.governance.decision.rationale')}</Label>
        <Textarea rows={3} {...register('rationale')} />
        {formState.errors.rationale && (
          <p className="text-xs text-destructive">{formState.errors.rationale.message}</p>
        )}
      </div>
      {showFollowOn ? (
        <div className="space-y-1">
          <Label>{t('workspace.governance.decision.followOnActions')}</Label>
          <Textarea rows={3} {...register('followOnActions')} />
        </div>
      ) : null}
      <div className="flex gap-2">
        <Button size="sm" onClick={decide(ProjectGovernanceDecision.Approved)} disabled={mutation.isPending}>
          {t('workspace.governance.decision.approve')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={decide(ProjectGovernanceDecision.Rejected)}
          disabled={mutation.isPending}
        >
          {t('workspace.governance.decision.reject')}
        </Button>
      </div>
    </form>
  );
}
